import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import SearchBox from 'components/site/SearchBox';
import CustomerSearchStore from 'stores/CustomerSearchStore';
import CustomersActionCreators from 'actions/CustomersActionCreators';
import { debounce, isEmpty } from 'lodash';
import { titleize, unescapeHTML } from 'underscore.string';
import { Translation, withTranslation } from 'react-i18next';
import LoadingCircle from 'components/ui/LoadingCircle';
import { getCarrierPreferences } from 'utils/CarrierPreferencesAPI';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import Badge from 'components/v2/Badge';
import { sluggify } from 'utils/localization/utils';

const roleStyles = {
  Shipper: 'brand',
  Carrier: 'blue',
  Driver: 'blue',
  Unauthorized: 'red',
  Vendor: 'purple',
  Consignee: 'pink',
};

export const renderItemWithPreferences = (item, isHiglighted) => {
  const unauthorized =
    (item.type === 'Carrier' || item.type === 'Driver') && !item.authorized;

  const color = unauthorized ? roleStyles.Unauthorized : roleStyles[item._type];
  const text = unauthorized ? 'Unauthorized' : item._type;

  const name =
    item.type === 'Consignee'
      ? unescapeHTML(item.company_name)
      : unescapeHTML(item.name);
  const carrier = unescapeHTML(item.carrier);

  return (
    <DropdownItem
      isHiglighted={isHiglighted}
      key={item.value}
      className="list-group-item"
    >
      <Translation>
        {(t, { i18n }) => (
          <div className="flex -mx-4">
            <div className="px-4 w-36">
              <Badge rounded color={color} className="label">
                {t(sluggify(text))}
              </Badge>
            </div>
            <div className="w-1/4 px-4">
              <span className="font-bold searchName">{name}</span>
              {item.carrier && (
                <div>
                  <span className="italic text-grey">{carrier}</span>
                </div>
              )}
            </div>
            {item.is_broker && (
              <div className="w-36 px-4">
                <Badge rounded color="orange" className="label">
                  {t('BROKER')}
                </Badge>
              </div>
            )}
            {item.preferred && (
              <Badge rounded color="green" className="label">
                {t('PREFERRED')}
              </Badge>
            )}
            {item.excluded && (
              <Badge rounded color="red" className="label">
                {t('EXCLUDED')}
              </Badge>
            )}
            {item._type !== 'Driver' && (
              <div className="w-1/2 px-4">
                <span className="text-xs text-grey" id="user-email">
                  {item.email}
                </span>
              </div>
            )}
          </div>
        )}
      </Translation>
    </DropdownItem>
  );
};

function getCustomerValue(item) {
  return titleize(item.optimized_name);
}

const USER_QUERY_TAGS = {
  'Third Party Carrier': 'third_party_carrier',
};

class CustomerSearchWithPreferences extends Component {
  static propTypes = {
    clearOnSelect: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    type: PropTypes.string,
    authorizedOnly: PropTypes.bool,
    defaultValue: PropTypes.string,
    placeholder: PropTypes.string,
    dropdownStyle: PropTypes.object,
    selectFields: PropTypes.array,
    disabled: PropTypes.bool,
    userTag: PropTypes.string,
    carrierPreferences: PropTypes.object,
    isDriver: PropTypes.bool,
  };

  static defaultProps = {
    authorizedOnly: false,
    clearOnSelect: true,
    type: '',
    dropdownStyle: {},
    selectFields: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      customers: [],
      selectedItem: '',
    };
    this.handleSearch = debounce(this.handleSearch, 300);
  }

  async componentDidMount() {
    CustomerSearchStore.addChangeListener(this.handleCustomerStoreChange);
  }

  componentWillUnmount() {
    CustomerSearchStore.removeChangeListener(this.handleCustomerStoreChange);
    CustomerSearchStore.clearSearchResults();
  }

  handleCustomerStoreChange = () => {
    const { isDriver, carrierPreferences } = this.props;
    let customers = CustomerSearchStore.getSearchResults();

    if (carrierPreferences) {
      customers = customers.map((customerItem) => {
        const carrierId = isDriver
          ? customerItem?.carrierData._id
          : customerItem._id;
        if (!carrierId) {
          return customerItem;
        }
        const excluded = carrierPreferences.excluded.includes(carrierId);
        const preferred = carrierPreferences.preferred.includes(carrierId);

        return {
          ...customerItem,
          preferred,
          excluded,
        };
      });
    }

    const filteredByAuth = customers.reduce(
      ([pass, fail], elem) => {
        return elem.authorized
          ? [[...pass, elem], fail]
          : [pass, [...fail, elem]];
      },
      [[], []],
    );
    const authorized = filteredByAuth[0].reduce(
      ([pass, fail], elem) => {
        return elem.preferred
          ? [[...pass, elem], fail]
          : [pass, [...fail, elem]];
      },
      [[], []],
    );
    const last = authorized[1].reduce(
      ([pass, fail], elem) => {
        return !elem.excluded
          ? [[...pass, elem], fail]
          : [pass, [...fail, elem]];
      },
      [[], []],
    );

    this.setState({
      customers: [...authorized[0], ...last.flat(1), ...filteredByAuth[1]],
    });
  };

  clear() {
    this.setState({ customers: [] });
  }

  handleChange = (nGram) => {
    if (!nGram || nGram.length <= 2) {
      CustomerSearchStore.clearSearchResults();
    }

    if (this.props.onChange) {
      this.props.onChange(nGram);
    }
  };

  handleSearch(nGram) {
    const {
      type: customerType,
      selectFields,
      authorizedOnly,
      userTag,
    } = this.props;
    const params = {
      name: nGram,
      selectFields,
      authorizedOnly,
    };

    if (customerType) {
      params.customerType = customerType;
    }

    if (userTag) {
      const tag = USER_QUERY_TAGS[userTag];
      if (tag) {
        params.userTag = tag;
      }
    }

    CustomersActionCreators.searchCustomers(params);
  }

  handleUserSelect = (value, item) => {
    const rejectSelection =
      this.props.assignField === true &&
      this.isCarrierOrDriver(item) &&
      !item.authorized;

    if (rejectSelection && !this.props.allowUnauthorizedCarriers) {
      return;
    }

    CustomerSearchStore.clearSearchResults();
    this.props.onSelect(value, item);
    const filteredValue = (value || '').trim();
    if (filteredValue) {
      const value = this.props.clearOnSelect ? '' : item.name;
      this.setState({
        selectedItem: value,
      });
    }
  };

  isCarrierOrDriver(item) {
    return item.type === 'Driver' || item.type === 'Carrier';
  }

  render() {
    const { t, carrierPreferences } = this.props;

    if (!carrierPreferences) {
      return (
        <div className="h-9">
          <LoadingCircle text={t('LOADING')} />
        </div>
      );
    }

    return (
      <SearchBox
        id={this.props.id}
        items={this.state.customers}
        clear={() => this.clear()}
        clearOnSelect={this.props.clearOnSelect}
        search={(nGram) => this.handleSearch(nGram)}
        placeholder={this.props.placeholder || t('SEARCH_FOR_CUSTOMERS')}
        value={this.state.selectedItem || this.props.defaultValue}
        menuStyle={this.props.menuStyle}
        onChange={(value) => this.handleChange(value)}
        onSelect={this.handleUserSelect}
        getItemValue={this.props.displayValue || getCustomerValue}
        renderItem={renderItemWithPreferences}
        inputStyle={this.props.inputStyle}
        dropdownStyle={this.props.dropdownStyle}
        disabled={this.props.disabled}
      />
    );
  }
}

export default withRouter(withTranslation()(CustomerSearchWithPreferences));
